import { ContentType, IMAGE_CONTENT_TYPES } from '@jurnee/common/src/constants/ContentTypes';
import { resizeImage } from '@jurnee/common/src/utils/images';
import { ChangeEvent, DragEvent, MouseEvent, RefObject, useRef, useState } from 'react';

interface UseFileInputProps {
  defaultName?: string;
  defaultSize?: number;
  defaultIdentifier?: number;
  imageMaxWidth?: number;
  onChange?(file: File): void;
  onRemove?(identifier: number): void;
}

export interface UseFileInputReturn {
  name: string | undefined;
  size: number | undefined;
  identifier: number | undefined;
  dragActive: boolean;
  hasFile: boolean;
  fileInput: RefObject<HTMLInputElement | null>;
  handleClick(): void;
  handleChange(e: ChangeEvent<HTMLInputElement>): Promise<void>;
  handleDrop(e: DragEvent<HTMLDivElement>): Promise<void>;
  handleDrag(e: DragEvent<HTMLDivElement>): void;
  handleRemove(e: MouseEvent): void;
}

export function useFileInput(props: UseFileInputProps): UseFileInputReturn {
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState<string | undefined>(props.defaultName);
  const [size, setSize] = useState<number | undefined>(props.defaultSize);
  const [identifier, setIdentifier] = useState<number | undefined>(props.defaultIdentifier);
  const [dragActive, setDragActive] = useState(false);

  const hasFile = !!name && !!size;

  function handleClick() {
    fileInput.current?.click();
  }

  function handleFileChange(file: File) {
    setName(file?.name);
    setSize(file?.size);
    setIdentifier(file?.lastModified);
    setDragActive(false);
    props.onChange?.(file);
  }

  async function handleFile(file: File) {
    if (!file) {
      return null;
    }

    if (props.imageMaxWidth && IMAGE_CONTENT_TYPES.includes(file.type as ContentType)) {
      const resizedImage = await resizeImage(file, props.imageMaxWidth);
      return handleFileChange(resizedImage);
    }

    return handleFileChange(file);
  }

  async function handleChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    e.stopPropagation();
    e.preventDefault();

    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }

  async function handleDrop(e: React.DragEvent<HTMLDivElement>): Promise<void> {
    e.stopPropagation();
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }

  function handleDrag(e: React.DragEvent<HTMLDivElement>) {
    e.stopPropagation();
    e.preventDefault();

    if (['dragenter', 'dragover'].includes(e.type)) {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }

  function handleRemove(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();

    const currentIdentifier = identifier;

    setName(undefined);
    setSize(undefined);
    setIdentifier(undefined);
    setDragActive(false);

    if (currentIdentifier !== undefined) {
      props.onRemove?.(currentIdentifier);
    }
  }

  return {
    name,
    size,
    identifier,
    dragActive,
    hasFile,
    fileInput,
    handleClick,
    handleChange,
    handleDrop,
    handleDrag,
    handleRemove,
  };
}